import React from "react";
import { useSettings } from "../../lib/useSettings";
import { useAuth } from "../../lib/auth";
import { useLocation } from "react-router-dom";
import { Wrench, Palmtree, Clock, Eye, EyeOff, Lock, FileText } from "lucide-react";

/**
 * Gate the whole public site based on site_status.
 *  - online       : everyone sees the site
 *  - draft        : visitor = splash "brouillon", admin = sees site with banner overlay
 *  - private      : visitor = splash "privé", admin = sees site with banner overlay
 *  - maintenance  : visitor = splash maintenance, admin = sees site
 */
export default function SiteGate({ children }) {
  const { settings: s } = useSettings();
  const { user } = useAuth();
  const loc = useLocation();

  // Always allow /admin/* and auth routes
  if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/account/login") || loc.pathname.startsWith("/account/register")) {
    return children;
  }

  const isAdmin = user?.role === "admin" || user?.role === "client";
  const status = s?.site_status || "online";

  // Visitor (non-admin) → splash according to status
  if (!isAdmin) {
    if (status === "maintenance") {
      return <SiteSplash variant="maintenance" message={s.site_maintenance_message} image={s.site_maintenance_image} companyName={s.company_name} />;
    }
    if (status === "draft") {
      return <SiteSplash variant="draft" message={s.site_draft_message} image={s.site_draft_image} companyName={s.company_name} />;
    }
    if (status === "private") {
      return <SiteSplash variant="private" message={s.site_draft_message} image={s.site_draft_image} companyName={s.company_name} />;
    }
  }

  return (
    <>
      {isAdmin && status !== "online" && <AdminPreviewBanner status={status} />}
      {children}
    </>
  );
}

function AdminPreviewBanner({ status }) {
  const map = {
    draft:       { Icon: FileText, label: "Brouillon", text: "Vous voyez le site en tant qu'admin. Les visiteurs voient un écran « Site en préparation »." , cls: "bg-slate-800 text-white" },
    private:     { Icon: Lock,     label: "Site privé", text: "Site privé : admins uniquement. Les visiteurs voient un écran d'accès restreint.", cls: "bg-indigo-800 text-white" },
    maintenance: { Icon: Wrench,   label: "Maintenance", text: "Site en maintenance. Les visiteurs voient un écran dédié.", cls: "bg-red-700 text-white" },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <div className={`w-full ${m.cls} px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2 sticky top-0 z-[60]`} data-testid="admin-preview-banner">
      <m.Icon className="w-4 h-4" />
      <span><strong>Mode {m.label}</strong> — {m.text}</span>
    </div>
  );
}

function SiteSplash({ variant, message, image, companyName }) {
  const presets = {
    draft: {
      Icon: FileText,
      badge: "Site en préparation",
      title: `${companyName || "Ce site"} est en cours de préparation`,
      defaultMsg: "Nous finalisons notre site internet. Revenez très bientôt pour le découvrir !",
      gradient: "from-slate-50 via-white to-sky-50",
      badgeCls: "bg-slate-100 text-slate-700",
      dotCls: "bg-slate-500",
      iconCls: "bg-[color:var(--brand-navy)] text-white",
    },
    private: {
      Icon: Lock,
      badge: "Accès restreint",
      title: `${companyName || "Ce site"} est privé`,
      defaultMsg: "Ce site est réservé. Merci de vous connecter ou de contacter l'administrateur.",
      gradient: "from-indigo-50 via-white to-slate-50",
      badgeCls: "bg-indigo-100 text-indigo-700",
      dotCls: "bg-indigo-500",
      iconCls: "bg-indigo-700 text-white",
    },
    maintenance: {
      Icon: Wrench,
      badge: "Maintenance en cours",
      title: `${companyName || "Le site"} est temporairement indisponible`,
      defaultMsg: "Nous effectuons une mise à jour importante. Le site sera de nouveau accessible dans quelques instants — merci de votre patience.",
      gradient: "from-red-50 via-white to-amber-50",
      badgeCls: "bg-red-100 text-red-700",
      dotCls: "bg-red-500",
      iconCls: "bg-red-500 text-white",
    },
  };
  const p = presets[variant] || presets.draft;
  const Ic = p.Icon;
  return (
    <section className={`min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br ${p.gradient}`} data-testid={`site-splash-${variant}`}>
      <div className="max-w-xl w-full text-center">
        {image ? (
          <img src={image} alt="" className="mx-auto mb-8 max-w-xs rounded-3xl shadow-xl" />
        ) : (
          <div className={`mx-auto mb-8 w-24 h-24 rounded-3xl ${p.iconCls} flex items-center justify-center shadow-xl`}>
            <Ic className="w-12 h-12 animate-pulse" />
          </div>
        )}
        <div className={`inline-flex items-center gap-2 ${p.badgeCls} text-xs uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full mb-5`}>
          <span className={`w-1.5 h-1.5 rounded-full ${p.dotCls} animate-pulse`} /> {p.badge}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)] mb-4">
          {p.title}
        </h1>
        <p className="text-[hsl(var(--foreground))]/70 leading-relaxed text-base">
          {message || p.defaultMsg}
        </p>
      </div>
    </section>
  );
}

// Backward-compat alias
export const MaintenanceSplash = ({ message, image, companyName }) => (
  <SiteSplash variant="maintenance" message={message} image={image} companyName={companyName} />
);

/**
 * Banner shown on /shop pages when shop_status is vacation/catalog/maintenance.
 */
export function ShopStatusBanner() {
  const { settings: s } = useSettings();
  const status = s?.shop_status;
  if (!s?.shop_enabled || !status || status === "online") return null;

  const map = {
    vacation:    { Icon: Palmtree, title: "Nous sommes en congés", cls: "from-amber-100 via-amber-50 to-orange-50 text-amber-900 border-amber-300" },
    catalog:     { Icon: Clock,    title: "Mode catalogue", cls: "from-sky-100 via-sky-50 to-blue-50 text-sky-900 border-sky-300" },
    maintenance: { Icon: Wrench,   title: "Boutique en maintenance", cls: "from-red-100 via-red-50 to-orange-50 text-red-900 border-red-300" },
    draft:       { Icon: EyeOff,   title: "Boutique en brouillon (admin uniquement)", cls: "from-slate-100 via-slate-50 to-white text-slate-800 border-slate-300" },
  };
  const meta = map[status];
  if (!meta) return null;

  return (
    <div className={`border-b-2 bg-gradient-to-r ${meta.cls}`} data-testid="shop-status-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-5 flex items-start md:items-center gap-4">
        {s.shop_status_image ? (
          <img src={s.shop_status_image} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-white/70 shrink-0 flex items-center justify-center">
            <meta.Icon className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base md:text-lg">{meta.title}</div>
          {s.shop_status_message && <p className="text-sm mt-0.5 leading-snug">{s.shop_status_message}</p>}
          {status === "vacation" && s.shop_vacation_until && (
            <p className="text-xs mt-1 font-bold opacity-80">Reprise des commandes le {new Date(s.shop_vacation_until).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Banner shown on /reservation when booking_status is draft/vacation.
 */
export function BookingStatusBanner() {
  const { settings: s } = useSettings();
  const status = s?.booking_status;
  if (!s?.booking_enabled || !status || status === "online") return null;
  const map = {
    vacation: { Icon: Palmtree, title: "Réservations en pause", cls: "from-amber-100 via-amber-50 to-orange-50 text-amber-900 border-amber-300" },
    draft:    { Icon: EyeOff,   title: "Réservations en brouillon (admin uniquement)", cls: "from-slate-100 via-slate-50 to-white text-slate-800 border-slate-300" },
  };
  const meta = map[status];
  if (!meta) return null;
  return (
    <div className={`border-b-2 bg-gradient-to-r ${meta.cls}`} data-testid="booking-status-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/70 shrink-0 flex items-center justify-center">
          <meta.Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base md:text-lg">{meta.title}</div>
          {s.booking_status_message && <p className="text-sm mt-0.5 leading-snug">{s.booking_status_message}</p>}
          {status === "vacation" && s.booking_vacation_until && (
            <p className="text-xs mt-1 font-bold opacity-80">Reprise des réservations le {new Date(s.booking_vacation_until).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
          )}
        </div>
      </div>
    </div>
  );
}
